/* eslint react-hooks/exhaustive-deps: "warn" */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvHero.module.scss';

type RowStatus = 'ok' | 'diff' | 'bad' | 'add';

type CardRow = [string, string, string, RowStatus];

type Card = {
  name: string;
  id: string;
  verdict: string;
  verdictType: 'ok' | 'bad' | 'warn';
  src: string;
  rows: CardRow[];
};

const CARDS: Card[] = [
  {
    name: 'Nordvik Trading AB',
    id: 'SE · Org.nr 556789-1234 · Bolagsverket',
    verdict: 'Verified',
    verdictType: 'ok',
    src: 'Source: Bolagsverket · Swedish Companies Registration Office',
    rows: [
      ['Legal name', 'Nordvik Trading AB', 'Match', 'ok'],
      ['Registration number', '556789-1234', 'Match', 'ok'],
      ['Company status', 'Active', 'Confirmed', 'ok'],
      ['Registered address', 'Sveavägen 44, Stockholm', 'Differs', 'diff'],
      ['VAT number', 'SE556789123401 · valid', 'Match', 'ok'],
      ['Officers', '2 resolved', 'Added', 'add'],
      ['Beneficial owners', '1 person · 62.5%', 'Added', 'add'],
      ['Sanctions & PEP', 'No match, 3 lists', 'Clear', 'ok'],
    ],
  },
  {
    name: 'Halden Logistikk AS',
    id: 'NO · Org.nr 9XXXXXXXX · Brønnøysund',
    verdict: 'Rejected',
    verdictType: 'bad',
    src: 'Source: Brønnøysundregistrene · Norway',
    rows: [
      ['Legal name', 'Halden Logistikk AS', 'Match', 'ok'],
      ['Registration number', '9XXXXXXXX', 'Match', 'ok'],
      ['Company status', 'Dissolved 14 Nov 2025', 'Failed', 'bad'],
      ['Registered address', 'Deregistered', 'Absent', 'bad'],
      ['VAT number', 'Deregistered', 'Failed', 'bad'],
      ['Officers', 'Last filing Sep 2025', 'Added', 'add'],
      ['Beneficial owners', 'Not held', 'Absent', 'add'],
      ['Sanctions & PEP', 'No match, 3 lists', 'Clear', 'ok'],
    ],
  },
  {
    name: 'Kowalski Handel sp. z o.o.',
    id: 'PL · KRS 0000XXXXXX · Krajowy Rejestr Sądowy',
    verdict: 'Escalate',
    verdictType: 'warn',
    src: 'Source: Krajowy Rejestr Sądowy · Poland',
    rows: [
      ['Legal name', 'Kowalski Handel sp. z o.o.', 'Match', 'ok'],
      ['Registration number', '0000XXXXXX', 'Match', 'ok'],
      ['Company status', 'Active', 'Confirmed', 'ok'],
      ['Incorporation date', '8 Apr 2026', 'Added', 'add'],
      ['VAT number', 'Not yet issued', 'Absent', 'diff'],
      ['Officers', 'D. Kowalski', 'Added', 'add'],
      ['Officer graph', '2 suspended sellers', 'Hit', 'bad'],
      ['Sanctions & PEP', 'No match, 3 lists', 'Clear', 'ok'],
    ],
  },
];

const CYCLE_MS = 5000;

const verdictClass = (type: Card['verdictType']) => {
  if (type === 'ok') return styles.verdictOk;
  if (type === 'bad') return styles.verdictBad;
  return styles.verdictWarn;
};

const rowStatusClass = (s: RowStatus) => {
  if (s === 'ok') return styles.statusOk;
  if (s === 'diff') return styles.statusDiff;
  if (s === 'bad') return styles.statusBad;
  return styles.statusAdd;
};

export const MsvHero = () => {
  const { t } = useInt();
  const [cardIndex, setCardIndex] = useState(0);
  const [visibleRows, setVisibleRows] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const card = CARDS[cardIndex];

  const startCycle = useCallback((idx: number) => {
    setCardIndex(idx);
    setVisibleRows([]);
    setProgress(0);

    const rows = CARDS[idx].rows;
    rows.forEach((_, i) => {
      setTimeout(() => {
        setVisibleRows((prev) => [...prev, i]);
      }, i * 320);
    });

    let elapsed = 0;
    const step = 60;
    if (progressRef.current) clearInterval(progressRef.current);
    progressRef.current = setInterval(() => {
      elapsed += step;
      setProgress(Math.min((elapsed / CYCLE_MS) * 100, 100));
      if (elapsed >= CYCLE_MS) {
        if (progressRef.current) clearInterval(progressRef.current);
      }
    }, step);
  }, []);

  useEffect(() => {
    startCycle(0);
    intervalRef.current = setInterval(() => {
      setCardIndex((prev) => {
        const next = (prev + 1) % CARDS.length;
        startCycle(next);
        return next;
      });
    }, CYCLE_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [startCycle]);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Approve real sellers in seconds. Stop the rest at signup.')}
            description={t(
              'Your signup form sends a name and a number. We resolve it against the government register that issued it, screen the officers and owners behind it, and return a decision your compliance team can defend.',
            )}
          />
          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/free-trial">
              {t('Start a free trial')}
            </Button>
          </div>
        </div>

        <div className={styles.terminal}>
          <div className={styles.termBar}>
            <span className={styles.termEp}>POST /v2/verify/trader</span>
            <span className={styles.termLive}>
              <span className={styles.pulse} />
              Live
            </span>
          </div>

          <div className={styles.termEntity}>
            <div>
              <div className={styles.termName}>{card.name}</div>
              <div className={styles.termId}>{card.id}</div>
            </div>
            <span className={`${styles.termVerdict} ${verdictClass(card.verdictType)}`}>{card.verdict}</span>
          </div>

          <div className={styles.termRows}>
            {card.rows.map((row, i) => (
              <div
                key={`${cardIndex}-${i}`}
                className={`${styles.trow} ${visibleRows.includes(i) ? styles.trowOn : ''}`}
              >
                <span className={styles.tKey}>{row[0]}</span>
                <span className={styles.tVal}>{row[1]}</span>
                <span className={`${styles.tStatus} ${rowStatusClass(row[3])}`}>{row[2]}</span>
              </div>
            ))}
          </div>

          <div className={styles.termProgress}>
            <div className={styles.termProgressBar} style={{ width: `${progress}%` }} />
          </div>

          <div className={styles.termFoot}>
            <span>{card.src}</span>
            <span>Synced 26 Jul 2026</span>
          </div>
        </div>
      </div>
    </Section>
  );
};
